import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../lib/hooks/useToast';
import { supabase } from '../lib/supabase';
import { OAuthCallbackLayout } from '../components/oauth/OAuthCallbackLayout';

export function MetaAdsCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { error: toastError, success: toastSuccess } = useToast();
  const [processingCallback, setProcessingCallback] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);
  const completedRef = useRef(false);

  useEffect(() => {
    // Prevent double processing in React 18 strict mode
    if (processedRef.current) {
      return;
    }
    processedRef.current = true;

    const processCallback = async () => {
      if (completedRef.current) return;

      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');

        if (errorParam) {
          console.error('[MetaAdsCallback] Error from Meta:', errorParam, errorDescription);
          setError(errorDescription || 'Errore durante l\'autorizzazione Meta');
          toastError(`Errore: ${errorDescription || errorParam}`);
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        if (!code || !state) {
          setError('Parametri mancanti nella risposta');
          toastError('Errore: parametri mancanti nella risposta');
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        // Validate state from database
        const { data: stateData, error: stateError } = await supabase
          .from('oauth_states')
          .select('id, organization_id, is_used, expires_at')
          .eq('state', state)
          .eq('service', 'meta_ads')
          .single();

        if (stateError || !stateData) {
          setError('Sessione non valida');
          toastError('Errore: sessione non valida');
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        // Check expiration
        if (new Date(stateData.expires_at) < new Date()) {
          setError('Sessione scaduta');
          toastError('Errore: sessione scaduta');
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        // Check already used (the edge function marks is_used=true atomically)
        if (stateData.is_used) {
          setError('Sessione già utilizzata');
          toastError('Errore: sessione già utilizzata');
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        // Exchange code for token via edge function (edge function handles CSRF marking)
        const response = await supabase.functions.invoke('meta-ads-token-exchange', {
          body: {
            code,
            state,
            organization_id: stateData.organization_id,
          },
        });

        if (response?.error) {
          const errorMessage = response.error?.message || 'Errore sconosciuto';
          setError(`Errore durante lo scambio del token: ${errorMessage}`);
          toastError('Errore durante lo scambio del token');
          setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
          return;
        }

        if (response?.data?.success === true) {
          completedRef.current = true;
          setProcessingCallback(false);
          toastSuccess('Connessione a Meta Ads completata con successo!');
          navigate('/settings?tab=integrations-hub&integration=meta-ads&meta_ads_result=success', { replace: true });
          return;
        }

        setError('Risposta non valida dal server');
        toastError('Errore: risposta non valida dal server');
        setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
      } catch (err) {
        if (completedRef.current) return;
        console.error('[MetaAdsCallback] Unexpected error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
        setError(`Errore durante l'elaborazione: ${errorMessage}`);
        toastError('Errore durante l\'elaborazione del callback');
        setProcessingCallback(false);
        setTimeout(() => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }), 3000);
      }
    };

    processCallback();
  }, [navigate, location.search]);

  const metaLogo = (
    <svg className="h-10 w-10" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="meta-cb-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0064E1" />
          <stop offset="100%" stopColor="#00BFFF" />
        </linearGradient>
      </defs>
      <path
        fill="url(#meta-cb-gradient)"
        d="M18 0C8.06 0 0 8.06 0 18s8.06 18 18 18 18-8.06 18-18S27.94 0 18 0zm6.79 24.32c-1.21 1.91-3.04 2.92-5.27 2.92-2.97 0-5.34-1.79-7.55-5.21-2.7 4.27-4.5 5.21-6.79 5.21-1.49 0-2.71-.71-3.62-2.04-.78-1.16-1.21-2.79-1.21-4.61 0-3.04 1.04-6.34 2.79-9 1.86-2.79 4-4.18 6.5-4.18 2.93 0 5.21 1.84 7.18 4.79 1.96-2.95 4.25-4.79 7.18-4.79 1.46 0 2.79.61 3.86 1.79 1.14 1.25 1.71 2.96 1.71 5.07 0 3.43-1.18 6.86-2.96 9.93l-1.82.12z"
      />
    </svg>
  );

  if (processingCallback) {
    return (
      <OAuthCallbackLayout
        status="processing"
        title="Connessione in corso"
        description="Stiamo completando la connessione con Meta Ads."
        providerLogo={metaLogo}
        footerNote="Verrai reindirizzato automaticamente al termine."
      />
    );
  }

  if (error) {
    return (
      <OAuthCallbackLayout
        status="error"
        title="Connessione non riuscita"
        description={error}
        providerLogo={metaLogo}
        primaryAction={{
          label: 'Torna alle impostazioni',
          onClick: () => navigate('/settings?tab=integrations-hub&integration=meta-ads', { replace: true }),
        }}
        footerNote="Verrai reindirizzato automaticamente."
      />
    );
  }

  return (
    <OAuthCallbackLayout
      status="success"
      title="Meta Ads collegato"
      description="La connessione con Meta Ads è attiva. Ora puoi monitorare lead e campagne dal CRM."
      providerLogo={metaLogo}
      primaryAction={{
        label: 'Vai alle impostazioni',
        onClick: () =>
          navigate('/settings?tab=integrations-hub&integration=meta-ads&meta_ads_result=success', { replace: true }),
      }}
    />
  );
}

export default MetaAdsCallback;
